import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Both axes, because a box can be overrun either way.
 *
 * Copy that wraps overruns vertically, which is the common case. A single word longer than the
 * box — "Schwierigkeiten", "Moderately" in a circle — cannot wrap at all, so it overruns
 * horizontally while the height stays put. Checking only scrollHeight leaves that one clipped
 * at whatever size it started.
 *
 * Both readings work through a centring flex box as well as a plain block: content centred on an
 * axis overflows symmetrically, and the scroll measurement still picks up the half past the end
 * edge, so any excess at all shows up.
 */
const overflows = node => node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth;

/**
 * One font size for every box in a set: the largest that fits ALL of them.
 *
 * Fitting each box independently (which is what useFitText does) sizes each one to its own copy,
 * so two cards in the same row — or two bubbles on the same scale — end up at different sizes
 * purely because one string is longer. That reads as sloppy rather than responsive. Measuring
 * every box and taking the smallest size any of them needs keeps the set uniform while still
 * adapting to a language whose copy runs long.
 *
 * The caller is responsible for giving each measured node a bounded size — `flex: 1` plus
 * `overflow: hidden` on a card, an explicit width/height on a bubble's text box. Left at its
 * automatic size a box grows with its own content, scroll and client dimensions stay equal, and
 * overflow is undetectable.
 *
 * Measurement writes directly to the nodes' inline style and clears it again, rather than
 * re-rendering per candidate size: a render per step would mean a dozen React passes per screen,
 * and an inline font-size left behind would override the value styled-components applies.
 *
 * @param {number} nodeCount - remeasure when the number of boxes changes
 * @param {{minFontSize: number, maxFontSize: number, remeasureOn: *}} range - `remeasureOn` is
 *   any value that changes when something OUTSIDE the copy has resized the boxes. The observer
 *   below catches those too, but only after a paint; passing the driving value here keeps the
 *   correction in the same pre-paint pass. Omit it where the boxes are sized by the layout alone.
 * @returns {{fontSize: number, registerCard: function(number): function(HTMLElement): void}}
 */
const useUniformFontSize = (nodeCount, { minFontSize, maxFontSize, remeasureOn }) => {
    const [fontSize, setFontSize] = useState(maxFontSize);
    const nodesRef = useRef([]);
    const frameRef = useRef(null);

    const measure = useCallback(() => {
        const nodes = nodesRef.current.filter(Boolean);
        if (nodes.length === 0) return;

        // A box with no laid-out height cannot be measured; the observer will call back.
        if (nodes.every(node => node.clientHeight === 0)) return;

        const fitsAt = size => {
            nodes.forEach(node => {
                node.style.fontSize = `${size}px`;
            });
            return !nodes.some(overflows);
        };

        // Every candidate is a forced layout of every box, before the first paint, so try as few as
        // possible: the designed size first, as the common case, then halve the range. Overflow only
        // grows with the font, so the largest size that fits is a single boundary to search for.
        // The minimum is the floor and is never measured — below it we accept clipping.
        let candidate = maxFontSize;

        if (!fitsAt(maxFontSize)) {
            let low = minFontSize;
            let high = maxFontSize - 1;

            while (low < high) {
                const middle = Math.ceil((low + high) / 2);
                if (fitsAt(middle)) {
                    low = middle;
                } else {
                    high = middle - 1;
                }
            }

            candidate = low;
        }

        // Hand sizing back to the stylesheet so the committed value is the one that renders.
        nodes.forEach(node => {
            node.style.fontSize = "";
        });

        setFontSize(candidate);
    }, [minFontSize, maxFontSize]);

    useLayoutEffect(() => {
        const nodes = nodesRef.current.filter(Boolean);
        if (nodes.length === 0) return undefined;

        // Straight away, and from a layout effect, so the size is settled before the browser
        // paints. Deferred to a frame callback — which runs AFTER a paint — the first frame
        // showed the designed maximum and then collapsed to the size that fits: 40px down to
        // 23px on a medium bubble scale, which reads as the bubble reshaping as it appears.
        measure();

        // Later resizes still go through a frame. A box width changes with the layout around it
        // — a bento span, a bubble growing to fill its column — and the observer reports those
        // after a paint regardless, so coalescing them in a frame keeps a measurement, which
        // writes to the very nodes being observed, from re-entering it.
        const scheduleMeasure = () => {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(measure);
        };

        const observer = new ResizeObserver(scheduleMeasure);
        nodes.forEach(node => observer.observe(node));

        return () => {
            cancelAnimationFrame(frameRef.current);
            observer.disconnect();
        };
    }, [measure, nodeCount, remeasureOn]);

    const registerCard = useCallback(
        index => node => {
            nodesRef.current[index] = node;
        },
        []
    );

    return { fontSize, registerCard };
};

export default useUniformFontSize;
